import * as React from 'react'
// antd's OWN DisabledContext, not a transcribed copy — see the same note in
// `./Checkbox.shadcn.tsx`.
import DisabledContext from 'antd/es/config-provider/DisabledContext'
import type { RadioRef } from 'antd/es/radio/interface'

import {
  RadioGroup as RadioGroupPrimitive,
  RadioGroupItem,
} from 'src/components/ui/radio-group'
import { cn } from 'src/lib/utils'

import type {
  RadioChangeEvent,
  RadioGroupButtonStyle,
  RadioGroupOptionType,
  RadioGroupProps,
  RadioProps,
} from './antd-props'
import { useStableId } from './useStableId'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Radio` seam. `./Radio.tsx` is the
 * dispatcher that chooses between this and antd's own Radio; nothing else
 * imports this file, and nothing reaches it until someone flips the design
 * toggle (`src/design-mode`).
 *
 * Started from the `update-design` reference branch's `src/antd/Radio.tsx`.
 * Divergences from the reference are marked `[development]`.
 *
 * The EXTERNAL API is antd's own `RadioProps` / `RadioGroupProps`, so the 73
 * files that import `Radio` from `src/antd` switch with no edit.
 *
 * [development] `.ant-radio*` IS NOT RE-EMITTED, for the reason the whole
 * presentational batch settled: antd's rules are unlayered and carry the dot
 * geometry, the pill chrome and the label spacing this seam replaces. The
 * parts are keyed by `data-slot`, and the four live stylesheets that reached
 * into `.ant-radio-button-wrapper` grew a `[data-slot='radio-button-wrapper']`
 * selector beside it. `data-state="checked"` is what those rules read instead
 * of `.ant-radio-button-wrapper-checked`.
 *
 * RADIX IS STRING-VALUED, antd IS NOT. `RadioGroup.Root` speaks
 * `value`/`onValueChange` in strings; antd radios here carry booleans, numbers
 * and enums (`<Radio value={false}>`). Each item registers its stringified key
 * against its real value in a group-held map, so the group hands the ORIGINAL
 * value back out of Radix's string callback and every `e.target.value`
 * consumer keeps its type. Two options whose values stringify identically
 * would collide — that group is already broken on antd, which compares with
 * `===`, so nothing here makes it worse.
 *
 * [development] THREE THINGS THE REFERENCE DROPPED are implemented here,
 * because live call sites pass them:
 *   • `buttonStyle` (10 call sites). antd's `solid` fills the selected pill
 *     and `outline` only tints its border and text; the reference hard-coded
 *     the filled look, so an `outline` group would have read as solid.
 *   • `size` (6 call sites) — antd's small/middle/large pill heights.
 *   • The JOINED PILL ROW. antd's button group is one control with shared
 *     borders, not a row of separate chips, and `RouteRadioSwitch` and
 *     `FilterMenuDatePicker` both restyle it on that assumption.
 * Also corrected: antd's Group fires `onChange` ONLY when the value actually
 * changes. Re-clicking the selected pill is a no-op there and is here too —
 * `RouteRadioSwitch` pushes a route on every change, so an echoing group
 * would push a duplicate history entry on every click.
 *
 * [development] THE CHANGE EVENT'S `nativeEvent` IS SYNTHESIZED here exactly
 * as it is in `./Checkbox.shadcn.tsx`, so `stopPropagation()` /
 * `preventDefault()` called from an `onChange` handler are no-ops in shadcn
 * mode; read that file's header for the full note. No call site does either
 * today.
 *
 * THE LABEL IS PAIRED BOTH WAYS (nesting plus `htmlFor`), with the same
 * caller-supplied-duplicate-id caveat and the same lint reason — again, see
 * `./Checkbox.shadcn.tsx`'s header.
 * ------------------------------------------------------------------------- */

interface RadioGroupContextValue {
  /** The selected value's Radix key, or `''` while nothing is selected. */
  selectedKey: string
  disabled?: boolean
  name?: string
  optionType: RadioGroupOptionType
  buttonStyle: RadioGroupButtonStyle
  size: RadioGroupProps['size']
  registerValue: (key: string, val: unknown) => void
}

const RadioGroupContext = React.createContext<RadioGroupContextValue | null>(
  null
)

/**
 * `Radio.Button` forces the pill rendering for ITSELF even inside a Group
 * whose `optionType` is the default — antd allows the two to be mixed, and
 * carries that through a context of its own (`RadioOptionTypeContext`).
 */
const RadioOptionTypeContext = React.createContext<RadioGroupOptionType | null>(
  null
)

const keyOf = (value: unknown) =>
  value === undefined || value === null ? '' : String(value)

const buildChangeEvent = (
  props: RadioProps,
  nativeEvent: MouseEvent
): RadioChangeEvent => ({
  target: { ...props, checked: true },
  stopPropagation: () => nativeEvent.stopPropagation(),
  preventDefault: () => nativeEvent.preventDefault(),
  nativeEvent,
})

/** antd's `.ant-radio-button-wrapper` heights, per `Radio.Group size`. */
const BUTTON_SIZE: Record<string, string> = {
  small: 'h-6 px-2 text-sm',
  large: 'h-10 px-4 text-base',
  middle: 'h-8 px-4 text-sm',
}

// forwardRef: React 18 runtime (no ref-as-prop). The handle is antd's
// `RadioRef` shape rather than the bare button the reference exposes, so a
// caller that reaches for `.focus()` finds what it finds on the antd path.
const RadioBase = React.forwardRef<RadioRef, RadioProps>(
  (
    {
      className,
      style,
      defaultChecked,
      checked: checkedProp,
      disabled: disabledProp,
      onChange,
      onClick,
      onMouseEnter,
      onMouseLeave,
      onKeyDown,
      value,
      children,
      id,
      autoFocus,
      name,
      required,
      tabIndex,
      title,
      // antd-only props that must never reach the DOM
      prefixCls: _prefixCls,
      rootClassName: _rootClassName,
      onKeyPress: _onKeyPress,
      skipGroup: _skipGroup,
      type: _type,
      ...rest
    },
    ref
  ) => {
    const group = React.useContext(RadioGroupContext)
    const forcedOptionType = React.useContext(RadioOptionTypeContext)
    const contextDisabled = React.useContext(DisabledContext)
    const key = keyOf(value)
    const inGroup = group !== null

    const buttonRef = React.useRef<HTMLButtonElement>(null)
    const generatedId = useStableId('radio')
    const resolvedId = id ?? generatedId

    const [uncontrolledChecked, setUncontrolledChecked] = React.useState(
      defaultChecked ?? false
    )
    const checked = inGroup
      ? group.selectedKey === key
      : checkedProp ?? uncontrolledChecked
    // antd's own merge, operator for operator — with ONE combination where it
    // is not the merge actually in force: the Group passes its `disabled` to
    // `RadioGroupPrimitive`, and Radix's own `RadioGroupItem` ORs the Root's
    // `disabled` with the item's. So `<Radio disabled={false}>` inside a
    // `<Radio.Group disabled>` renders an enabled-LOOKING wrapper (this merge
    // yields `false`) over a control Radix has made non-interactive, where
    // antd would render it fully enabled. No call site does that today — all
    // nine `Radio.Group disabled` sites disable the whole group uniformly. If
    // one ever needs the per-option override, stop passing `disabled` to
    // `RadioGroupPrimitive` and let each item carry the merged value itself.
    const disabled = disabledProp ?? group?.disabled ?? contextDisabled
    const optionType = forcedOptionType ?? group?.optionType ?? 'default'
    const isButton = optionType === 'button'

    // Registered during render, as the reference does: the group needs the
    // mapping before its own `onValueChange` can fire, and an effect would
    // arrive a commit too late for a group that renders and is clicked in the
    // same interaction.
    const registerValue = group?.registerValue
    if (inGroup) registerValue?.(key, value)

    React.useImperativeHandle(ref, () => ({
      focus: (options?: FocusOptions) => buttonRef.current?.focus(options),
      blur: () => buttonRef.current?.blur(),
      input: buttonRef.current as unknown as HTMLInputElement | null,
      nativeElement: buttonRef.current,
    }))

    const handleStandaloneChange = () => {
      if (checkedProp === undefined) setUncontrolledChecked(true)
      onChange?.(
        buildChangeEvent(
          { ...rest, value, disabled, id: resolvedId, name, required, title },
          new MouseEvent('click')
        )
      )
    }

    const item = (
      <RadioGroupItem
        {...rest}
        ref={buttonRef}
        id={resolvedId}
        value={inGroup ? key : 'on'}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        // Only when the call site asked for one. Radix's RovingFocusGroup is
        // what makes a radio group ONE tab stop, and it does that by handing
        // the item a `tabIndex` through `asChild` — where an explicit
        // `undefined` from here WINS, silently turning the group back into one
        // tab stop per option.
        {...(tabIndex === undefined ? {} : { tabIndex })}
        onClick={onClick}
        onKeyDown={onKeyDown}
        className={isButton ? 'sr-only' : undefined}
      />
    )

    // antd always wraps a Radio in a `<label>`, with or without children.
    // `data-state` carries the "checked" signal the live stylesheets used to
    // read off `.ant-radio-button-wrapper-checked`.
    const wrapper = (
      <label
        data-slot={isButton ? 'radio-button-wrapper' : 'radio-wrapper'}
        data-state={checked ? 'checked' : 'unchecked'}
        data-disabled={disabled || undefined}
        htmlFor={resolvedId}
        title={title}
        className={cn(
          'cursor-pointer',
          isButton
            ? cn(
                'inline-flex items-center justify-center border transition-colors',
                // One joined control, not a row of chips: the pills share
                // borders and only the ends are rounded.
                '-ml-px first:ml-0 first:rounded-l-md last:rounded-r-md',
                BUTTON_SIZE[group?.size ?? 'middle'] ?? BUTTON_SIZE.middle,
                // The selected/disabled colours are branched in JS rather
                // than by a `data-[disabled]:` variant: two variants that set
                // the same property have equal specificity, so which one wins
                // is Tailwind's own emission order, not the order they are
                // written in — and a disabled group would keep the one
                // saturated pill in an otherwise read-only row. `data-state`
                // still carries the signal for the four live stylesheets that
                // read it.
                disabled
                  ? cn(
                      'border-input bg-muted text-muted-foreground',
                      checked && 'z-10'
                    )
                  : cn(
                      'border-input bg-background text-foreground hover:text-primary',
                      group?.buttonStyle === 'solid'
                        ? 'data-[state=checked]:z-10 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground'
                        : 'data-[state=checked]:z-10 data-[state=checked]:border-primary data-[state=checked]:text-primary'
                    )
              )
            : // `items-center`, not `items-baseline`: the dot is a 16px box
              // with no text baseline of its own. See `./Checkbox.shadcn.tsx`.
              'inline-flex items-center gap-2 text-sm',
          // As `./Checkbox.shadcn.tsx`: grey the label, and let the control
          // fade itself, rather than stacking two opacities on the control.
          // The pill sets its own disabled colours above, so this only has to
          // carry the cursor for it.
          disabled &&
            (isButton
              ? 'cursor-not-allowed'
              : 'cursor-not-allowed text-muted-foreground'),
          className
        )}
        style={style}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {item}
        {children !== undefined && (
          <span data-slot="radio-label">{children}</span>
        )}
      </label>
    )

    if (inGroup) return wrapper

    // Standalone Radio (no Group ancestor): host a private single-item Root so
    // Radix's select semantics work at all — `<Radio checked={…} />` is how
    // the radio row-selection tables are written.
    return (
      <RadioGroupPrimitive
        // '' (not undefined) keeps Radix CONTROLLED while nothing is selected;
        // `undefined` makes it uncontrolled and it warns on every flip.
        value={checked ? 'on' : ''}
        onValueChange={handleStandaloneChange}
        className="contents"
        name={name}
        disabled={disabled}
      >
        {wrapper}
      </RadioGroupPrimitive>
    )
  }
)
RadioBase.displayName = 'Radio'

const RadioButton = React.forwardRef<RadioRef, RadioProps>((props, ref) => (
  <RadioOptionTypeContext.Provider value="button">
    <RadioBase ref={ref} {...props} />
  </RadioOptionTypeContext.Provider>
))
RadioButton.displayName = 'Radio.Button'

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      className,
      style,
      options,
      disabled,
      defaultValue,
      value: valueProp,
      onChange,
      size,
      name,
      children,
      id,
      optionType = 'default',
      buttonStyle = 'outline',
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      // antd-only props that must never reach the DOM
      prefixCls: _prefixCls,
      rootClassName: _rootClassName,
      ...restProps
    },
    ref
  ) => {
    // antd's `RadioGroupProps` types no `onKeyDown` and antd's own Group
    // forwards none — it puts only `pickAttrs(props, {aria, data})` on its
    // root. This seam spreads its whole rest onto the Radix Root, so one CAN
    // arrive here (an untyped caller, or a spread props object), and setting
    // `onKeyDown={handleArrowKeys}` after the spread would silently drop it.
    // Pulled out and composed instead, so it runs first and can act — see
    // `handleArrowKeys` below.
    const { onKeyDown, ...rest } = restProps as typeof restProps & {
      onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
    }

    const [uncontrolledValue, setUncontrolledValue] =
      React.useState<unknown>(defaultValue)
    const value = valueProp !== undefined ? valueProp : uncontrolledValue
    const selectedKey = keyOf(value)

    const valueMapRef = React.useRef(new Map<string, unknown>())
    const registerValue = React.useCallback((key: string, val: unknown) => {
      valueMapRef.current.set(key, val)
    }, [])

    // The key a dispatch has ALREADY emitted, so the arrow handler below and
    // Radix's own arrow-to-select cannot both fire for one keystroke. Cleared
    // on a microtask, i.e. at the end of the task that dispatched the event —
    // never on a re-render, because a CONTROLLED group whose owner rejects the
    // change never re-renders and must still accept the next attempt.
    const emittedRef = React.useRef<string | null>(null)

    const emit = (key: string) => {
      // antd's Group fires onChange only on a real change.
      if (key === selectedKey || key === emittedRef.current) return
      emittedRef.current = key
      Promise.resolve().then(() => {
        emittedRef.current = null
      })
      const original = valueMapRef.current.has(key)
        ? valueMapRef.current.get(key)
        : key
      if (valueProp === undefined) setUncontrolledValue(original)
      onChange?.(
        buildChangeEvent(
          { value: original, disabled, name },
          new MouseEvent('click')
        )
      )
    }

    /**
     * ARROW KEYS MOVE THE SELECTION, not only the focus.
     *
     * That is what a native radio group does — it is antd's behaviour here
     * (its items are real `<input type="radio">`) and what WAI-ARIA's
     * radiogroup pattern requires. Radix aims for it too, but its mechanism is
     * a `document`-level `keydown` listener that sets a ref which its item's
     * `onFocus` then reads: the roving-focus handler is a REACT handler on the
     * item, so it moves focus while the event is still below `document`, and
     * the ref is still false when the focus fires. Confirmed in Chrome against
     * this branch's radix-ui 1.6.7 — the antd group selected on ArrowRight and
     * the Radix one only moved focus.
     *
     * So the seam does it, on the Root. This is a BUBBLE-phase handler, so it
     * runs AFTER the item's own keydown handler, not before it: the reason
     * `document.activeElement` is still the PREVIOUS item by the time this
     * reads it is that Radix's `RovingFocusGroupItem` defers its focus move
     * into a `setTimeout`. Verified against this branch's radix-ui 1.6.7. If a
     * future Radix moves focus synchronously, this would compute from the NEW
     * active element and skip an option; the guard is the arrow-key cases in
     * `src/antd/__tests__/radio-seam-parity.test.tsx`, which assert the
     * selected value after each keystroke. Focus is still Radix's job; only
     * the selection is added, and `emit` above is what keeps a keystroke from
     * being counted twice if Radix's own path does fire.
     *
     * An `onKeyDown` that reached this seam through the rest spread runs
     * FIRST, so it can `preventDefault()` or otherwise act before the arrow
     * logic rather than being overwritten by it. antd's Group honours none at
     * all, so this can only ever add behaviour, never contradict it.
     */
    const ARROW_STEP: Record<string, number> = {
      ArrowRight: 1,
      ArrowDown: 1,
      ArrowLeft: -1,
      ArrowUp: -1,
    }

    const handleArrowKeys = (event: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event)
      const step = ARROW_STEP[event.key]
      if (!step) return
      const items = Array.from(
        event.currentTarget.querySelectorAll<HTMLElement>(
          '[data-slot="radio-group-item"]:not([disabled])'
        )
      )
      const from = items.indexOf(document.activeElement as HTMLElement)
      if (from === -1) return
      // Same wrap-around Radix's roving focus uses, so the two agree on where
      // the focus lands and which value goes with it.
      const next = items[(from + step + items.length) % items.length]
      emit(next.getAttribute('value') ?? '')
    }

    const contextValue = React.useMemo(
      () => ({
        selectedKey,
        disabled,
        name,
        optionType,
        buttonStyle,
        size,
        registerValue,
      }),
      [
        selectedKey,
        disabled,
        name,
        optionType,
        buttonStyle,
        size,
        registerValue,
      ]
    )

    const ItemComp = optionType === 'button' ? RadioButton : RadioBase

    return (
      <RadioGroupContext.Provider value={contextValue}>
        <RadioGroupPrimitive
          {...rest}
          ref={ref}
          id={id}
          data-slot="radio-group"
          value={selectedKey}
          onValueChange={emit}
          onKeyDown={handleArrowKeys}
          disabled={disabled}
          name={name}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onFocus={onFocus}
          onBlur={onBlur}
          className={cn(
            'inline-flex flex-row flex-wrap items-center',
            // The dot rendering is a row of separate choices; the button one
            // is a single joined control, so it carries no gap of its own.
            optionType === 'button' ? 'align-middle' : 'gap-x-2',
            className
          )}
          style={style}
        >
          {options && options.length > 0
            ? options.map((option) => {
                const opt =
                  typeof option === 'string' || typeof option === 'number'
                    ? { label: option, value: option }
                    : option
                return (
                  <ItemComp
                    key={String(opt.value)}
                    value={opt.value}
                    disabled={
                      'disabled' in opt ? opt.disabled || disabled : disabled
                    }
                    style={opt.style}
                    title={opt.title}
                    id={opt.id}
                  >
                    {opt.label}
                  </ItemComp>
                )
              })
            : children}
        </RadioGroupPrimitive>
      </RadioGroupContext.Provider>
    )
  }
)
RadioGroup.displayName = 'Radio.Group'

export const Radio = Object.assign(RadioBase, {
  Group: RadioGroup,
  Button: RadioButton,
})
